import React, {useMemo, useState} from "react";
import {Input, Select, InputNumber, Tooltip, Switch, Tag, Button} from "antd";
import {UndoOutlined, SearchOutlined, CaretDownOutlined, CaretRightOutlined} from "@ant-design/icons";
import {PlanProfiles} from "../ProjectUploader";
import ResultTable from "../../components/table/ResultTable";
import ResultHeader from "../../components/table/ResultHeader";
import ResultBody from "../../components/table/ResultBody";
import ResultRow from "../../components/table/ResultRow";
import ResultHeaderCell from "../../components/table/ResultHeaderCell";
import ResultBodyCell from "../../components/table/ResultBodyCell";
import MessageCodePreviewModal from "./MessageCodePreviewModal";

const CHANNEL_CONTAIN_PROFILE_OPTIONS = ['YR2', 'YR5', 'YR10', 'YR25', 'YR50', 'YR100', 'YR500'];

const SECTIONS = [
    {
        key: 'surcharge',
        title: 'Surcharge & Water Surface',
        fields: [
            {kind: 'range', label: 'Surcharge', unit: 'ft', minKey: 'delta_ws_lt', maxKey: 'delta_ws_gt', desc: 'FW 01, FW 01 IB'},
            {kind: 'single', label: 'Head Loss', unit: 'ft', key: 'head_loss', desc: 'XS 05, XS 05b'},
            {kind: 'single', label: 'Velocity Head Change', unit: 'ft', key: 'velocity_head', desc: 'XS 05, XS 05d'},
            {kind: 'single', label: 'Conv. Ratio Range', key: 'conv_ratio', desc: 'XS 05, XS 05c'},
        ],
    },
    {
        key: 'slopes',
        title: 'Slopes',
        fields: [
            {
                kind: 'modeRange', label: 'Thalweg Slope',
                modeKey: 'inv_slope_threshold_max_mode', modeDesc: 'XS 09',
                modeAppliesTo: 'max',
                minKey: 'inv_slope_threshold', minDesc: 'XS 07', minProps: {min: -1, max: 100},
                maxKey: 'inv_slope_threshold_max', maxDesc: 'XS 09', maxProps: {min: 0, max: 100},
            },
            {kind: 'range', label: 'Normal Depth Slope', minKey: 'normal_depth_slope_min', maxKey: 'normal_depth_slope_max', desc: 'FL 07', minProps: {min: 0, max: 100}, maxProps: {min: 0, max: 100}},
            {kind: 'range', label: 'Culvert Slope', minKey: 'culvert_slope_min', maxKey: 'culvert_slope_threshold', minDesc: 'ST 13', maxDesc: 'ST 14', minProps: {min: -1, max: 0}, maxProps: {min: 0, max: 1}},
            {kind: 'single', label: 'Bank Station Slope', key: 'bs_slope', desc: 'XS 06', props: {min: -1, max: 100}},
        ],
    },
    {
        key: 'geometry',
        title: 'Geometry / Cross-Section',
        fields: [
            {kind: 'single', label: 'XS Topo Diff. Significance', unit: '%', key: 'area_tol', desc: 'IS 03', props: {min: 0, max: 100}, scale: 100},
            {kind: 'single', label: 'Channel Width Dev. from Reach Avg', unit: '%', key: 'reach_average_dev', desc: 'XS 08', props: {min: 0, max: 100}, defaultFallback: 0},
            {kind: 'single', label: 'Channel Width Dev. from US/DS Avg', unit: '%', key: 'bounding_average_dev', desc: 'XS 08', props: {min: 0, max: 100}, defaultFallback: 0},
            {kind: 'single', label: 'RS Diff', unit: 'ft', key: 'rs_diff', desc: 'XY 12'},
            {kind: 'single', label: 'DSRL Tol', unit: 'ft', key: 'dsrl_tol', desc: 'XY 11', props: {min: 0, max: 9999999}},
            {kind: 'single', label: 'Overbank DSRL Tol', unit: '%', key: 'overbank_dsrl_tol', desc: 'XY 15'},
            {kind: 'single', label: 'Max OB to Channel DS Length Ratio', key: 'max_ob_channel', desc: 'XS 02'},
            {kind: 'modeSingle', label: 'Max DSRL', unit: 'ft', modeKey: 'max_dsrl_mode', key: 'max_dsrl', desc: 'XS 05, XS 05a'},
            {kind: 'single', label: 'Deflection Angle', unit: '°', key: 'deflection_angle', desc: 'XY 19', props: {min: 0, max: 180}, defaultFallback: 90},
            {kind: 'single', label: 'Median Times', key: 'e_times', desc: 'XS 05, XS 05a', defaultFallback: 1.5},
            {kind: 'single', label: 'Thalweg Times', key: 'thalweg_times', desc: 'XS 09', defaultFallback: 1.5},
        ],
    },
    {
        key: 'structures',
        title: 'Structures',
        fields: [
            {kind: 'single', label: 'Max Skew Angle', unit: '°', key: 'skew_angle', desc: 'XY 05, XY 06'},
            {kind: 'single', label: 'Skew Deviation', unit: '%', key: 'skew_deviation', desc: 'XY 05, XY 06', props: {min: 0, max: 100}, defaultFallback: 25},
            {kind: 'single', label: 'Structure Station Multiplier', key: 'structure_multiplier', desc: 'LB 07, LB 08', props: {min: 1, max: 100}},
            {kind: 'range', label: 'Expansion Ratio', minKey: 'expansion_ratio_min', maxKey: 'expansion_ratio_max', desc: 'ST 01', minDefault: 1, maxDefault: 4},
            {kind: 'single', label: 'Transition Ratio Tol', key: 'transition_ratio_tol', desc: 'ST 01', props: {min: 0, max: 1}},
            {kind: 'single', label: 'Embankment Side Slope (H:V)', key: 'embankment_side', desc: 'ST 10', props: {min: 0, precision: 0, step: 1}, defaultFallback: 3},
            {
                kind: 'modeRange', label: 'US/DS Distance',
                modeKey: 'structure_distance_mode',
                minKey: 'structure_distance_min',
                maxKey: 'structure_distance_max',
                desc: 'ST 10',
                modeAppliesTo: 'both',
                minDefault: 5, maxDefault: 10,
            },
        ],
    },
    {
        key: 'flow',
        title: 'Flow & Operations',
        fields: [
            {kind: 'single', label: 'Drawdown', key: 'drawdown_default', desc: 'OP 04', props: {min: 0, max: 100}, defaultFallback: 0},
            {kind: 'single', label: 'Drawdown Slope', key: 'drawdown_slope_default', desc: 'OP 04', props: {min: -1, max: 100}, defaultFallback: 0, formatDefault: v => (typeof v === 'number' ? v.toFixed(4) : v)},
            {kind: 'single', label: 'Crossing', key: 'crossing_default', desc: 'OP 03', props: {min: 0, max: 100}, defaultFallback: 0},
            {kind: 'single', label: 'Flow Change Rate', unit: '%', key: 'flow_change_rate', desc: 'FL 03', props: {min: 0, max: 100}, defaultFallback: 0},
            {kind: 'single', label: 'Encroachment Placement Tol', unit: 'ft', key: 'enc_placement_tol', desc: 'FW 02', props: {min: 0, max: 100}, defaultFallback: 0.1},
            {kind: 'profile', label: 'Channel Contain Profile Threshold', key: 'channel_contain_profile', desc: 'XS 10', defaultFallback: 'YR10'},
        ],
    },
];

const INPUT_W = 100;
const SELECT_W = 90;

const isSet = (v) => v !== undefined && v !== null;

const fmtNum = (v) => {
    if (!isSet(v)) return '—';
    if (typeof v !== 'number') return String(v);
    return v === 0 ? '0.00' : v;
};

const labelWithUnit = (label, unit) => unit ? `${label} (${unit})` : label;

function fieldIsModified(field, values, defValues) {
    const probe = (k) => isSet(values?.[k]) && values?.[k] !== defValues?.[k];
    if (field.kind === 'range') return probe(field.minKey) || probe(field.maxKey);
    if (field.kind === 'modeSingle') return probe(field.modeKey) || probe(field.key);
    if (field.kind === 'modeRange') return probe(field.modeKey) || probe(field.minKey) || probe(field.maxKey);
    return probe(field.key);
}

function fieldMatchesSearch(field, q) {
    if (!q) return true;
    const hay = [field.label, field.desc, field.minDesc, field.maxDesc, field.modeDesc].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
}

function fieldDescription(field) {
    if (field.desc) return field.desc;
    return [field.minDesc, field.maxDesc, field.modeDesc].filter(Boolean).join(' / ');
}

function splitSectionsBalanced(sections) {
    const weights = sections.map(s => s.fields.length + 1);
    const total = weights.reduce((a, b) => a + b, 0);
    const target = total / 2;
    const left = [], right = [];
    let cum = 0;
    for (let i = 0; i < sections.length; i++) {
        // Place in left until adding the next section would overshoot the midpoint by more than half its own weight
        if (cum + weights[i] / 2 <= target) {
            left.push(sections[i]);
            cum += weights[i];
        } else {
            right.push(sections[i]);
        }
    }
    if (left.length === 0 && right.length > 0) { left.push(right.shift()); }
    return [left, right];
}

function LabelCell({label, modified}) {
    return (
        <ResultBodyCell style={{textAlign: 'left', padding: '2px 6px'}}>
            <span style={{fontSize: 12}}>{label}</span>
            {modified && (
                <Tag color="gold" style={{margin: '0 0 0 4px', fontSize: 9, padding: '0 3px', lineHeight: '14px'}}>mod</Tag>
            )}
        </ResultBodyCell>
    );
}

// Carries the modal trigger down through the render helpers without
// having to thread it as a prop through every renderSingle/Range/etc.
const PreviewCtx = React.createContext(() => {});

// `text` is a separator-delimited list of message codes (e.g. "XS 05, XS 05b"
// or "ST 13 / ST 14" — `fieldDescription` joins range/mode fields with " / ").
// Each code becomes a small clickable tag that opens the preview modal.
function DescriptionCell({text}) {
    const onPick = React.useContext(PreviewCtx);
    const codes = Array.from(new Set(
        String(text || '')
            .split(/[,/]/)
            .map(s => s.trim())
            .filter(Boolean)
    ));
    return (
        <ResultBodyCell style={{textAlign: 'left', padding: '2px 6px', fontSize: 11, color: '#666'}}>
            {codes.length === 0 ? (
                <span style={{color: '#aaa'}}>—</span>
            ) : (
                codes.map(code => (
                    <Tag
                        key={code}
                        style={{cursor: 'pointer', marginInlineEnd: 4, marginBottom: 2, fontSize: 10, padding: '0 4px'}}
                        onClick={() => onPick && onPick(code)}
                    >
                        {code}
                    </Tag>
                ))
            )}
        </ResultBodyCell>
    );
}

const EMPTY_CELL_STYLE = {padding: 0};

function EmptyCell() {
    return <ResultBodyCell style={EMPTY_CELL_STYLE}/>;
}

function ModeCell({modeKey, mode, onChange}) {
    return (
        <ResultBodyCell style={{textAlign: 'center', padding: 0}}>
            <Select
                size="small"
                style={{width: SELECT_W}}
                value={mode}
                onChange={(v) => onChange(modeKey, v)}
            >
                <Select.Option value="auto">Auto</Select.Option>
                <Select.Option value="manual">Manual</Select.Option>
            </Select>
        </ResultBodyCell>
    );
}

function InputCell({value, onChange, disabled, props = {}}) {
    return (
        <ResultBodyCell style={{textAlign: 'center', padding: 0}}>
            <InputNumber
                size="small"
                style={{width: INPUT_W}}
                controls={false}
                value={value}
                onChange={onChange}
                step={props.step ?? 0.01}
                precision={props.precision ?? 2}
                min={props.min}
                max={props.max}
                disabled={disabled}
            />
        </ResultBodyCell>
    );
}

function SelectCell({value, onChange, options}) {
    return (
        <ResultBodyCell style={{textAlign: 'center', padding: 0}}>
            <Select size="small" style={{width: INPUT_W}} value={value} onChange={onChange}>
                {options}
            </Select>
        </ResultBodyCell>
    );
}

function DefaultCell({text, modified, onReset}) {
    return (
        <ResultBodyCell style={{textAlign: 'left', padding: '2px 6px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4}}>
                <span style={{fontSize: 10, color: '#888', fontStyle: 'italic'}}>{text}</span>
                {modified && (
                    <Tooltip title="Reset to default">
                        <Button
                            size="small"
                            type="text"
                            icon={<UndoOutlined style={{fontSize: 10}}/>}
                            onClick={onReset}
                            style={{padding: '0 3px', height: 18, minWidth: 18}}
                        />
                    </Tooltip>
                )}
            </div>
        </ResultBodyCell>
    );
}

function renderSingle(field, values, defValues, onChange) {
    const {key, label, unit, desc, props = {}, scale = 1, defaultFallback, formatDefault} = field;
    const raw = values?.[key];
    const def = defValues?.[key] ?? defaultFallback;
    const displayVal = isSet(raw) ? raw * scale : (isSet(def) ? def * scale : undefined);
    const modified = isSet(raw) && raw !== def;
    const defText = formatDefault ? formatDefault(def) : fmtNum(def);
    const handle = (v) => onChange(key, scale !== 1 ? (isSet(v) ? v / scale : v) : v);

    return (
        <ResultRow style={{background: modified ? '#fffbe6' : undefined}}>
            <LabelCell label={labelWithUnit(label, unit)} modified={modified}/>
            <EmptyCell/>
            <InputCell value={displayVal} onChange={handle} props={props}/>
            <EmptyCell/>
            <DefaultCell text={defText} modified={modified} onReset={() => onChange(key, def)}/>
            <DescriptionCell text={fieldDescription(field)}/>
        </ResultRow>
    );
}

function renderRange(field, values, defValues, onChange) {
    const {label, unit, minKey, maxKey, desc, minDesc, maxDesc, minProps = {}, maxProps = {}, minDefault, maxDefault} = field;
    const minRaw = values?.[minKey];
    const maxRaw = values?.[maxKey];
    const minDef = defValues?.[minKey] ?? minDefault;
    const maxDef = defValues?.[maxKey] ?? maxDefault;
    const minVal = isSet(minRaw) ? minRaw : minDef;
    const maxVal = isSet(maxRaw) ? maxRaw : maxDef;
    const modified = (isSet(minRaw) && minRaw !== minDef) || (isSet(maxRaw) && maxRaw !== maxDef);
    return (
        <ResultRow style={{background: modified ? '#fffbe6' : undefined}}>
            <LabelCell label={labelWithUnit(label, unit)} modified={modified}/>
            <EmptyCell/>
            <InputCell value={minVal} onChange={(v) => onChange(minKey, v)} props={minProps}/>
            <InputCell value={maxVal} onChange={(v) => onChange(maxKey, v)} props={maxProps}/>
            <DefaultCell
                text={`${fmtNum(minDef)} – ${fmtNum(maxDef)}`}
                modified={modified}
                onReset={() => { onChange(minKey, minDef); onChange(maxKey, maxDef); }}
            />
            <DescriptionCell text={fieldDescription(field)}/>
        </ResultRow>
    );
}

function renderModeSingle(field, values, defValues, onChange) {
    const {label, unit, modeKey, key, desc, props = {}} = field;
    const modeDef = defValues?.[modeKey] ?? 'auto';
    const mode = values?.[modeKey] ?? modeDef;
    const isAuto = mode === 'auto';
    const def = defValues?.[key];
    const raw = values?.[key];
    const value = isSet(raw) ? raw : def;
    const modified = (isSet(values?.[modeKey]) && values?.[modeKey] !== modeDef) || (isSet(raw) && raw !== def);

    return (
        <ResultRow style={{background: modified ? '#fffbe6' : undefined}}>
            <LabelCell label={labelWithUnit(label, unit)} modified={modified}/>
            <ModeCell modeKey={modeKey} mode={mode} onChange={onChange}/>
            <InputCell value={value} onChange={(v) => onChange(key, v)} disabled={isAuto} props={props}/>
            <EmptyCell/>
            <DefaultCell
                text={`${modeDef} · ${fmtNum(def)}`}
                modified={modified}
                onReset={() => { onChange(modeKey, modeDef); onChange(key, def); }}
            />
            <DescriptionCell text={fieldDescription(field)}/>
        </ResultRow>
    );
}

function renderModeRange(field, values, defValues, onChange) {
    const {label, unit, modeKey, minKey, maxKey, desc, modeAppliesTo = 'both', minProps = {}, maxProps = {}, minDefault, maxDefault} = field;
    const modeDef = defValues?.[modeKey] ?? 'auto';
    const mode = values?.[modeKey] ?? modeDef;
    const isAuto = mode === 'auto';
    const minRaw = values?.[minKey];
    const maxRaw = values?.[maxKey];
    const minDef = defValues?.[minKey] ?? minDefault;
    const maxDef = defValues?.[maxKey] ?? maxDefault;
    const minVal = isSet(minRaw) ? minRaw : minDef;
    const maxVal = isSet(maxRaw) ? maxRaw : maxDef;
    const modified =
        (isSet(values?.[modeKey]) && values?.[modeKey] !== modeDef) ||
        (isSet(minRaw) && minRaw !== minDef) ||
        (isSet(maxRaw) && maxRaw !== maxDef);
    const minDisabled = (modeAppliesTo === 'both' || modeAppliesTo === 'min') && isAuto;
    const maxDisabled = (modeAppliesTo === 'both' || modeAppliesTo === 'max') && isAuto;

    return (
        <ResultRow style={{background: modified ? '#fffbe6' : undefined}}>
            <LabelCell label={labelWithUnit(label, unit)} modified={modified}/>
            <ModeCell modeKey={modeKey} mode={mode} onChange={onChange}/>
            <InputCell value={minVal} onChange={(v) => onChange(minKey, v)} disabled={minDisabled} props={minProps}/>
            <InputCell value={maxVal} onChange={(v) => onChange(maxKey, v)} disabled={maxDisabled} props={maxProps}/>
            <DefaultCell
                text={`${modeDef} · ${fmtNum(minDef)}–${fmtNum(maxDef)}`}
                modified={modified}
                onReset={() => {
                    onChange(modeKey, modeDef);
                    onChange(minKey, minDef);
                    onChange(maxKey, maxDef);
                }}
            />
            <DescriptionCell text={fieldDescription(field)}/>
        </ResultRow>
    );
}

function renderProfile(field, values, defValues, onChange) {
    const {label, key, desc, defaultFallback} = field;
    const def = defValues?.[key] ?? defaultFallback;
    const value = values?.[key] ?? def;
    const defLabel = PlanProfiles.find(p => p.value === def)?.label ?? def;
    const modified = isSet(values?.[key]) && values?.[key] !== def;
    const options = CHANNEL_CONTAIN_PROFILE_OPTIONS.map(k => {
        const lbl = PlanProfiles.find(p => p.value === k)?.label || k;
        return <Select.Option value={k} key={k}>{lbl}</Select.Option>;
    });

    return (
        <ResultRow style={{background: modified ? '#fffbe6' : undefined}}>
            <LabelCell label={label} modified={modified}/>
            <EmptyCell/>
            <SelectCell value={value} onChange={(v) => onChange(key, v)} options={options}/>
            <EmptyCell/>
            <DefaultCell text={defLabel} modified={modified} onReset={() => onChange(key, def)}/>
            <DescriptionCell text={fieldDescription(field)}/>
        </ResultRow>
    );
}

function renderField(field, values, defValues, onChange) {
    switch (field.kind) {
        case 'range':       return renderRange(field, values, defValues, onChange);
        case 'modeSingle':  return renderModeSingle(field, values, defValues, onChange);
        case 'modeRange':   return renderModeRange(field, values, defValues, onChange);
        case 'profile':     return renderProfile(field, values, defValues, onChange);
        default:            return renderSingle(field, values, defValues, onChange);
    }
}

function SectionRow({section, open, modCount, onToggle}) {
    return (
        <tr onClick={onToggle} style={{cursor: 'pointer'}}>
            <td
                colSpan={6}
                style={{
                    backgroundColor: '#fafafa',
                    borderTop: '1px solid #d0d0d0',
                    borderBottom: '1px solid #e0e0e0',
                    padding: '4px 8px',
                    fontWeight: 600,
                    fontSize: 12,
                    userSelect: 'none',
                }}
            >
                {open
                    ? <CaretDownOutlined style={{fontSize: 10, color: '#666'}}/>
                    : <CaretRightOutlined style={{fontSize: 10, color: '#666'}}/>}
                <span style={{marginLeft: 6}}>{section.title}</span>
                <span style={{color: '#999', fontWeight: 400, marginLeft: 6, fontSize: 11}}>
                    ({section.fields.length})
                </span>
                {modCount > 0 && (
                    <Tag color="gold" style={{marginLeft: 6, fontSize: 9, padding: '0 3px', lineHeight: '14px'}}>
                        {modCount} mod
                    </Tag>
                )}
            </td>
        </tr>
    );
}

export default function ThresholdSettingsV2({onValuesChange, values, defValues}) {
    const [search, setSearch] = useState('');
    const [onlyModified, setOnlyModified] = useState(false);
    const [openSections, setOpenSections] = useState(() => new Set(SECTIONS.map(s => s.key)));
    const [previewCode, setPreviewCode] = useState(null);

    const toggle = (key) => {
        setOpenSections(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const visibleSections = useMemo(() => {
        const q = search.trim().toLowerCase();
        return SECTIONS
            .map(section => ({
                ...section,
                fields: section.fields.filter(f =>
                    fieldMatchesSearch(f, q) && (!onlyModified || fieldIsModified(f, values, defValues))
                ),
            }))
            .filter(section => section.fields.length > 0);
    }, [search, onlyModified, values, defValues]);

    const totalModified = useMemo(
        () => SECTIONS.reduce((n, s) => n + s.fields.filter(f => fieldIsModified(f, values, defValues)).length, 0),
        [values, defValues]
    );

    const [leftSections, rightSections] = useMemo(
        () => splitSectionsBalanced(visibleSections),
        [visibleSections]
    );

    const renderTable = (sections) => (
        <ResultTable>
            <ResultHeader>
                <ResultRow>
                    <ResultHeaderCell style={{textAlign: 'left', width: 250}}>Variable</ResultHeaderCell>
                    <ResultHeaderCell style={{width: 95}}>Mode</ResultHeaderCell>
                    <ResultHeaderCell style={{width: 105}}>Value / Min</ResultHeaderCell>
                    <ResultHeaderCell style={{width: 105}}>Max</ResultHeaderCell>
                    <ResultHeaderCell style={{textAlign: 'left', width: 115}}>Default</ResultHeaderCell>
                    <ResultHeaderCell style={{textAlign: 'left', width: 140}}>Message Codes</ResultHeaderCell>
                </ResultRow>
            </ResultHeader>
            <ResultBody>
                {sections.map(section => {
                    const isOpen = openSections.has(section.key);
                    const modCount = section.fields.filter(f => fieldIsModified(f, values, defValues)).length;
                    return (
                        <React.Fragment key={section.key}>
                            <SectionRow
                                section={section}
                                open={isOpen}
                                modCount={modCount}
                                onToggle={() => toggle(section.key)}
                            />
                            {isOpen && section.fields.map((f, i) => (
                                <React.Fragment key={f.key || f.minKey || f.modeKey || i}>
                                    {renderField(f, values, defValues, onValuesChange)}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    );
                })}
            </ResultBody>
        </ResultTable>
    );

    return (
        <PreviewCtx.Provider value={setPreviewCode}>
        <div className="threshold-v2-compact">
            <style>{`
                .threshold-v2-compact .ant-input-number-sm input {
                    height: 22px !important;
                    text-align: center;
                }
                .threshold-v2-compact .ant-select-sm .ant-select-selector {
                    height: 22px !important;
                }
                .threshold-v2-compact .ant-select-sm .ant-select-selection-item {
                    line-height: 20px !important;
                    font-size: 11px;
                }
                .threshold-v2-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 24px;
                    align-items: start;
                }
                @media (min-width: 1700px) {
                    .threshold-v2-grid {
                        grid-template-columns: minmax(0, 830px) minmax(0, 830px);
                    }
                }
            `}</style>
            <div style={{display: 'flex', gap: 12, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap'}}>
                <Input
                    prefix={<SearchOutlined style={{color: '#bbb', fontSize: 11}}/>}
                    placeholder="Search..."
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{width: 220}}
                    size="small"
                />
                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                    <Switch size="small" checked={onlyModified} onChange={setOnlyModified}/>
                    <span style={{fontSize: 11}}>Only modified</span>
                    {totalModified > 0 && (
                        <Tag color="gold" style={{margin: 0, fontSize: 9, padding: '0 3px', lineHeight: '14px'}}>
                            {totalModified}
                        </Tag>
                    )}
                </div>
            </div>

            {visibleSections.length === 0 ? (
                <div style={{padding: 16, textAlign: 'center', color: '#999', border: '1px dashed #e0e0e0', borderRadius: 4, fontSize: 12}}>
                    No thresholds match.
                </div>
            ) : (
                <div className="threshold-v2-grid">
                    {leftSections.length > 0 && renderTable(leftSections)}
                    {rightSections.length > 0 && renderTable(rightSections)}
                </div>
            )}
            <MessageCodePreviewModal
                open={!!previewCode}
                code={previewCode}
                onClose={() => setPreviewCode(null)}
            />
        </div>
        </PreviewCtx.Provider>
    );
}
